/**
 * Session seed + SAS for the two-browser handshake.
 *
 * A real Fume session splits the trust root R 2-of-2 across two independent
 * OOB channels and derives `dh` via a SPAKE2 PAKE (dh is NEVER transmitted).
 * This POC collapses that: the initiator generates the whole seed and ships it
 * inside the invite code, so pasting the invite (one OOB channel) hands the peer
 * the seed. The SAS below still lets both humans confirm they built the SAME
 * session before trusting it — that part IS faithful. Honest scope note, per the
 * project's "keep vanish claims honest" rule.
 */
import { sha256 } from '@noble/hashes/sha256';
import type { Transcript } from '@fume/core';

export interface SessionSeed {
  readonly R: Uint8Array;
  readonly dh: Uint8Array;
  readonly transcriptHash: Uint8Array;
  readonly sessionId: Uint8Array;
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}
function isAllZero(b: Uint8Array): boolean {
  return b.every((x) => x === 0);
}
function equal(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Initiator-side: fabricate a fresh shared seed (deriveKeys I1 guards satisfied). */
export function generateSeed(): SessionSeed {
  const R = randomBytes(32);
  let dh = randomBytes(32);
  while (isAllZero(dh) || equal(dh, R)) dh = randomBytes(32);
  const transcriptHash = randomBytes(32);
  if (isAllZero(transcriptHash)) transcriptHash[0] = 1;
  return { R, dh, transcriptHash, sessionId: randomBytes(32) };
}

/** The transcript shape SessionContainer expects (SAS-verified once humans confirm). */
export function transcriptFor(seed: SessionSeed): Transcript {
  return { hash: seed.transcriptHash, sasVerified: true };
}

const toHex = (b: Uint8Array): string =>
  Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
const fromHex = (h: string): Uint8Array => {
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
  return out;
};

export function seedToJson(seed: SessionSeed): Record<string, string> {
  return {
    R: toHex(seed.R),
    dh: toHex(seed.dh),
    t: toHex(seed.transcriptHash),
    s: toHex(seed.sessionId),
  };
}
function reqStr(o: Record<string, string>, k: string): string {
  const v = o[k];
  if (typeof v !== 'string') throw new Error(`invalid seed: missing field ${k}`);
  return v;
}
export function seedFromJson(o: Record<string, string>): SessionSeed {
  return {
    R: fromHex(reqStr(o, 'R')),
    dh: fromHex(reqStr(o, 'dh')),
    transcriptHash: fromHex(reqStr(o, 't')),
    sessionId: fromHex(reqStr(o, 's')),
  };
}

/**
 * Short Authentication String — a 6-digit code both sides derive from the shared
 * seed. Identical iff both built the same session; humans compare out-of-band.
 */
export function deriveSas(seed: SessionSeed): string {
  const material = new Uint8Array(96);
  material.set(seed.sessionId, 0);
  material.set(seed.transcriptHash, 32);
  material.set(seed.R, 64);
  const digest = sha256(material);
  const n = (((digest[0] ?? 0) << 16) | ((digest[1] ?? 0) << 8) | (digest[2] ?? 0)) % 1_000_000;
  const s = n.toString().padStart(6, '0');
  return `${s.slice(0, 3)} ${s.slice(3)}`;
}

/**
 * Seed <-> URL-fragment codec for the hosted-rendezvous share link.
 *
 * The seed is base64url-encoded into the link fragment (`#…&s=<seed>`). The
 * fragment is never sent to any server (not the relay, not the static host), so
 * this keeps the seed on the same peer-to-peer OOB path the copy/paste invite
 * used — the relay only ever relays SDP. Delivered in person, the QR of this
 * link carries the seed too, never crossing the wire.
 */
export function encodeSeedForLink(seed: SessionSeed): string {
  const json = JSON.stringify(seedToJson(seed));
  return btoa(unescape(encodeURIComponent(json)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function decodeSeedFromLink(code: string): SessionSeed {
  const b64 = code.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(escape(atob(b64)));
  return seedFromJson(JSON.parse(json) as Record<string, string>);
}
